import { delay } from "../client.js";

const STORAGE_KEY = "console-design.account-assets.v2";

const SEED_ASSETS = {
  "acc-1": [
    {
      id: "asset-1",
      name: "City ZX",
      assetTypeId: "asset-type-1",
      values: {
        manufacturer: "Honda",
        model: "City ZX",
        variant: "ZX CVT",
        registration: "KA-01-AB-1234",
        model_year: "2024",
        insurance_doc: "policy-2024.pdf",
      },
      createdAt: "2025-03-15T10:30:00.000Z",
      updatedAt: "2025-03-15T10:30:00.000Z",
    },
    {
      id: "asset-2",
      name: "Delivery Van",
      assetTypeId: "asset-type-2",
      values: {
        manufacturer: "Mahindra",
        model: "Supro",
        variant: "Maxi Truck",
        vin: "MA1TC2YEH5A123456",
        odometer: "45200",
      },
      createdAt: "2025-02-01T09:00:00.000Z",
      updatedAt: "2025-02-20T14:22:00.000Z",
    },
  ],
};

function cloneSeed() {
  return Object.fromEntries(
    Object.entries(SEED_ASSETS).map(([id, rows]) => [
      id,
      rows.map((row) => ({ ...row, values: { ...row.values } })),
    ]),
  );
}

function loadStore() {
  const merged = cloneSeed();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return merged;
    const parsed = JSON.parse(raw);
    for (const [accountId, rows] of Object.entries(parsed)) {
      if (Array.isArray(rows)) merged[accountId] = rows;
    }
  } catch {
    /* ignore */
  }
  return merged;
}

function saveStore(store) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
}

function nextId() {
  return `asset-${Date.now().toString(36)}`;
}

function getAccountAssets(store, accountId) {
  if (!store[accountId]) {
    store[accountId] = SEED_ASSETS[accountId]
      ? SEED_ASSETS[accountId].map((row) => ({ ...row, values: { ...row.values } }))
      : [];
  }
  return store[accountId];
}

function normalizeValues(values) {
  if (!values || typeof values !== "object") return {};
  return Object.fromEntries(
    Object.entries(values).map(([key, value]) => [String(key).trim(), String(value ?? "").trim()]),
  );
}

function deriveAssetName(values) {
  const model = values?.model?.trim();
  if (model) return model;
  const manufacturer = values?.manufacturer?.trim();
  const variant = values?.variant?.trim();
  if (manufacturer && variant) return `${manufacturer} ${variant}`;
  if (manufacturer) return manufacturer;
  const displayName = values?.display_name?.trim();
  if (displayName) return displayName;
  const first = Object.values(values ?? {}).find((value) => String(value).trim());
  if (first) return String(first).trim();
  return `Asset ${Date.now().toString(36)}`;
}

function validatePayload(payload) {
  const assetTypeId = payload.assetTypeId?.trim();
  if (!assetTypeId) throw new Error("Asset type is required");
  const values = normalizeValues(payload.values);
  const name = payload.name?.trim() || deriveAssetName(values);
  return { name, assetTypeId, values };
}

export function formatAssetTimestamp(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const date = d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "2-digit" });
  const time = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
  return `${date} | ${time}`;
}

export async function listAccountAssets(accountId) {
  await delay(120);
  const store = loadStore();
  return [...getAccountAssets(store, accountId)];
}

export async function createAccountAsset(accountId, payload) {
  await delay(140);
  const { name, assetTypeId, values } = validatePayload(payload);
  const store = loadStore();
  const rows = getAccountAssets(store, accountId);
  const now = new Date().toISOString();
  const row = { id: nextId(), name, assetTypeId, values, createdAt: now, updatedAt: now };
  rows.push(row);
  saveStore(store);
  return { ...row, values: { ...row.values } };
}

export async function updateAccountAsset(accountId, assetId, payload) {
  await delay(120);
  const { name, assetTypeId, values } = validatePayload(payload);
  const store = loadStore();
  const rows = getAccountAssets(store, accountId);
  const row = rows.find((r) => r.id === assetId);
  if (!row) throw new Error("Asset not found");
  row.name = name;
  row.assetTypeId = assetTypeId;
  row.values = values;
  row.updatedAt = new Date().toISOString();
  saveStore(store);
  return { ...row, values: { ...row.values } };
}

export async function deleteAccountAsset(accountId, assetId) {
  await delay(100);
  const store = loadStore();
  const rows = getAccountAssets(store, accountId);
  const index = rows.findIndex((r) => r.id === assetId);
  if (index < 0) throw new Error("Asset not found");
  rows.splice(index, 1);
  saveStore(store);
}
